import React, { createContext, useContext, useState, useEffect } from 'react';
import { registerUser } from '../services/api';
import { invalidateUsageCache } from '../services/usageService';
import { invalidateTeamUsageCache } from '../services/usageTeamService';
import { invalidateClubUsageCache } from '../services/usageClubService';

// SEC-001 extensao (jul/2026): os tres usage*Service mantem cache
// module-level (60s TTL) de dado POR-USUARIO (/me/usage*). O cache
// sobrevivia a troca de sessao — conta B lia billing/clube da conta A
// dentro da janela de TTL. Mesma classe do SEC-001-CLUB-DEFAULT-LOGOUT-LEAK
// (documentado no logout abaixo), camada service-cache que ficou de fora
// do fix original. Invalidar AQUI (na troca de identidade) e nao
// key-por-user_id: o service e deliberadamente ignorante de quem pergunta
// (identidade vive no token) — importar identidade pro cache acoplaria
// uma camada que nao a tem.
const invalidateUserScopedCaches = () => {
  invalidateUsageCache();
  invalidateTeamUsageCache();
  invalidateClubUsageCache();
};

/**
 * Contexto de Autenticação
 * Gerencia estado do usuário, login, logout e preferências
 */

const AuthContext = createContext();

// Hook para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Provider de Autenticação
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    favoriteLeagues: [],
    favoriteTeams: [],
    notifications: {
      liveMatches: true,
      favoriteTeams: true,
      goalAlerts: false
    },
    theme: 'light'
  });

  // Inicializa dados do usuário
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedUser = localStorage.getItem('arena_stats_user');
        const storedPreferences = localStorage.getItem('arena_stats_preferences');

        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }

        if (storedPreferences) {
          setPreferences(JSON.parse(storedPreferences));
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login com API real
  // INV-2c: inviteToken opcional — transportado ao backend para a exceção do
  // Launch Gate (conta pending + convite válido → JWT sem mudar status).
  // Chamadas existentes (2 args) permanecem idênticas.
  const login = async (email, password, inviteToken = null) => {
    try {
      setLoading(true);
      console.log('[AuthContext] 📤 Login chamado para:', email);
      localStorage.removeItem('arenastats_active_role');
      
      // MOCK LOGIN para desenvolvimento local (DISABLED in production)
      if (process.env.NODE_ENV === 'development' && email === 'paulobrant@arenastats.com.br' && password === '123456') {
        console.log('[AuthContext] 🔑 Login MOCK bem-sucedido');
        const mockUser = {
          id: 999,
          name: 'Paulo Brant',
          email: 'paulobrant@arenastats.com.br',
          role: 'super_admin',  // CRITICAL: Habilita acesso à governança
          user_type: 'coach',   // RBAC fallback → TREINADOR
          token: 'mock-token-' + Date.now(),
          avatar: null,
          isLoggedIn: true,
          loginTime: new Date().toISOString()
        };
        
        setUser(mockUser);
        localStorage.setItem('arena_stats_user', JSON.stringify(mockUser));
        return { success: true, user: mockUser };
      }
      
      // Chama API de login
      const { loginUser } = await import('../services/api');
      const response = await loginUser({
        email,
        password,
        ...(inviteToken ? { invite_token: inviteToken } : {}),
      });
      console.log('[AuthContext] ✅ Resposta do login:', response);
      
      if (response.success) {
        const loggedUser = {
          id: response.data.id,
          name: response.data.name,
          email: response.data.email,
          token: response.data.token, // IMPORTANTE: Salva o token!
          user_type: response.data.user_type || null, // RBAC fallback
          avatar: response.data.avatar_url || null,
          isLoggedIn: true,
          loginTime: new Date().toISOString()
        };

        setUser(loggedUser);
        localStorage.setItem('arena_stats_user', JSON.stringify(loggedUser));

        // Clean admin-only selector state so non-admins start fresh
        localStorage.removeItem('arenastats_selected_team');
        localStorage.removeItem('arenastats_selected_athlete');
        localStorage.removeItem('arenastats_selectors_hidden');

        // SEC-001 extensao: nova sessao nao herda usage-cache da anterior
        // (tab com sessao expirada -> login direto, sem logout no meio).
        invalidateUserScopedCaches();

        console.log('[AuthContext] 🔑 Token salvo no localStorage');

        return { success: true, user: loggedUser };
      } else {
        console.error('[AuthContext] ❌ Login falhou:', response.error);
        
        return { success: false, error: response.error || 'Erro ao fazer login' };
      }
    } catch (error) {
      console.error('[AuthContext] ❌ Erro no login:', error);
      
      // OPÇÃO A: Detecta ACCOUNT_AWAITING_LAUNCH e trata especificamente
      if (error.response?.data?.code === 'ACCOUNT_AWAITING_LAUNCH') {
        const accountStatus = error.response.data.account_status || 'pending';
        
        console.log('[AuthContext] ⏳ Conta aguardando lançamento');
        
        // Salva email temporariamente para mostrar na página
        localStorage.setItem('arena_stats_pending_user', JSON.stringify({
          email: email,
          account_status: accountStatus
        }));
        
        return { 
          success: false, 
          error: 'Aguardando lançamento',
          code: 'ACCOUNT_AWAITING_LAUNCH',
          accountStatus: accountStatus
        };
      }
      
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || error.message };
    } finally {
      setLoading(false);
    }
  };

  // Registro de usuário
  const register = async (userData) => {
    try {
      setLoading(true);
      console.log('[AuthContext] register chamado com:', { ...userData, password: '***' });
      
      const response = await registerUser(userData);
      console.log('[AuthContext] ✅ Resposta recebida:', response);
      console.log('[AuthContext] 🔍 Tipo da resposta:', typeof response);
      console.log('[AuthContext] 🔍 Keys da resposta:', Object.keys(response));
      
      // Backend pode retornar diferentes formatos
      // Formato 1: { success: true, data: {...} }
      // Formato 2: { id, name, email, token } direto
      
      let userData_response;
      let success = false;
      
      if (response.success === true || response.success === false) {
        // Formato com campo success
        success = response.success;
        userData_response = response.data || response.user;
        console.log('[AuthContext] 📦 Formato com success:', { success, userData_response });
      } else if (response.id && response.email) {
        // Backend retornou dados diretamente sem wrapper
        success = true;
        userData_response = response;
        console.log('[AuthContext] 📦 Formato direto (sem wrapper):', userData_response);
      }
      
      if (!success) {
        console.error('[AuthContext] ❌ Registro falhou:', response.error || response.message);
        return { success: false, error: response.error || response.message || 'Erro ao criar conta' };
      }
      
      if (!userData_response || !userData_response.id) {
        console.error('[AuthContext] ❌ Dados do usuário inválidos:', userData_response);
        return { success: false, error: 'Resposta do servidor inválida' };
      }
      
      console.log('[AuthContext] 🎯 Criando objeto newUser com userData_response:', userData_response);
      
      const newUser = {
        id: userData_response.id,
        name: userData_response.name,
        email: userData_response.email,
        token: userData_response.token || null,
        user_type: userData_response.user_type || null,
        avatar: null,
        isLoggedIn: !!userData_response.token,  // Only truly logged in if we have a token
        loginTime: new Date().toISOString()
      };

      console.log('[AuthContext] ✅ newUser criado:', { ...newUser, token: newUser.token ? '***' : 'null' });
      
      if (newUser.token) {
        // Has token (e.g. intent=club) → full auth state
        // SEC-001 extensao: register-com-token TAMBEM cria sessao nova sem
        // logout previo (user A logado cria conta B) — terceira porta da
        // mesma classe. Mesma invalidacao dos outros dois pontos.
        invalidateUserScopedCaches();
        setUser(newUser);
        localStorage.setItem('arena_stats_user', JSON.stringify(newUser));
        console.log('[AuthContext] 🎉 Registro concluído com token — sessão ativa!');
      } else {
        // No token (pending approval) → don't persist auth state
        console.log('[AuthContext] ⏳ Registro concluído sem token — aguardando aprovação');
      }
      
      return { success: true, user: newUser, hasToken: !!newUser.token };
    } catch (error) {
      console.error('[AuthContext] ❌ Exceção capturada:', error);
      console.error('[AuthContext] 🔍 Error.response:', error.response);
      console.error('[AuthContext] 🔍 Error.response.data:', error.response?.data);
      
      // Specific 409 handling: email already exists
      if (error.response?.status === 409) {
        return { success: false, error: 'Este email já está cadastrado. Tente fazer login ou use outro email.', code: 'EMAIL_DUPLICATE' };
      }
      
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao criar conta' };
    } finally {
      console.log('[AuthContext] 🏁 setLoading(false)');
      setLoading(false);
    }
  };

  // Logout
  const logout = () => {
    setUser(null);
    localStorage.removeItem('arena_stats_user');
    localStorage.removeItem('arena_stats_preferences');
    localStorage.removeItem('arenastats_active_role');
    // SEC-001-CLUB-DEFAULT-LOGOUT-LEAK: ActiveContext persists clubId to
    // localStorage. Without removing these keys, the next user logging into
    // the same tab loads the previous user's clubId (bypasses the RBAC
    // bootstrap hydration in ActiveContext, because `!clubId` is false).
    // - vukz_active_context_v1: current STORAGE_KEY (see ActiveContextContext L19)
    // - sucron_active_context_v1: LEGACY_KEY (see ActiveContextContext L20);
    //   loadSaved() migrates it to vukz_ on first mount, so leaving it behind
    //   would still leak via migration path on next login.
    localStorage.removeItem('vukz_active_context_v1');
    localStorage.removeItem('sucron_active_context_v1');
    // SEC-001 extensao: usage-caches module-level tambem sobrevivem ao
    // logout — mesma classe do leak acima, camada service-cache.
    invalidateUserScopedCaches();
    setPreferences({
      favoriteLeagues: [],
      favoriteTeams: [],
      notifications: {
        liveMatches: true,
        favoriteTeams: true,
        goalAlerts: false
      },
      theme: 'light'
    });
  };

  // Atualizar perfil
  const updateProfile = async (updatedData) => {
    try {
      const updatedUser = { ...user, ...updatedData };
      setUser(updatedUser);
      localStorage.setItem('arena_stats_user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      return { success: false, error: error.message };
    }
  };

  // Gerenciar preferências
  const updatePreferences = (newPreferences) => {
    const updated = { ...preferences, ...newPreferences };
    setPreferences(updated);
    localStorage.setItem('arena_stats_preferences', JSON.stringify(updated));
  };

  // Adicionar/remover liga favorita
  const toggleFavoriteLeague = (leagueId) => {
    const favorites = [...preferences.favoriteLeagues];
    const index = favorites.indexOf(leagueId);
    
    if (index > -1) {
      favorites.splice(index, 1);
    } else {
      favorites.push(leagueId);
    }
    
    updatePreferences({ favoriteLeagues: favorites });
  };

  // Adicionar/remover time favorito
  const toggleFavoriteTeam = (teamId) => {
    const favorites = [...preferences.favoriteTeams];
    const index = favorites.indexOf(teamId);
    
    if (index > -1) {
      favorites.splice(index, 1);
    } else {
      favorites.push(teamId);
    }
    
    updatePreferences({ favoriteTeams: favorites });
  };

  // Verificar se liga é favorita
  const isFavoriteLeague = (leagueId) => {
    return preferences.favoriteLeagues.includes(leagueId);
  };

  // Verificar se time é favorito
  const isFavoriteTeam = (teamId) => {
    return preferences.favoriteTeams.includes(teamId);
  };

  // Obter estatísticas do usuário
  const getUserStats = () => {
    if (!user) return null;

    return {
      favoriteLeagues: preferences.favoriteLeagues.length,
      favoriteTeams: preferences.favoriteTeams.length,
      memberSince: user.loginTime ? new Date(user.loginTime).toLocaleDateString('pt-BR') : 'N/A',
      lastLogin: user.loginTime ? new Date(user.loginTime).toLocaleDateString('pt-BR') : 'N/A'
    };
  };

  const value = {
    // Estado
    user,
    loading,
    preferences,
    isAuthenticated: !!user,

    // Ações
    login,
    register,
    logout,
    updateProfile,
    updatePreferences,

    // Favoritos
    toggleFavoriteLeague,
    toggleFavoriteTeam,
    isFavoriteLeague,
    isFavoriteTeam,

    // Utilitários
    getUserStats
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};